import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DiseaseRiskDateModel } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
interface ActiveRiskLevel {
  riskLevel: number;
  dateRangeDayNamesString: string;
  dateRangeDatesString: string;
}
@Component({
  selector: 'widget-week-days',
  imports: [SharedModule, TranslateModule],
  templateUrl: './widget-week-days.component.html',
  styleUrl: './widget-week-days.component.less',
})
export class WidgetWeekDaysComponent implements OnInit, OnChanges {
  @Input() diseaseDays: DiseaseRiskDateModel[];
  @Input() showRiskBadges = true;

  activeRiskLevels: ActiveRiskLevel[] = [];

  ngOnInit(): void {
    this.restartWeek();
  }

  restartWeek() {
    this.activeRiskLevels = [];
    if (this.diseaseDays && this.diseaseDays.length > 0) {
      // Sort by actual date to ensure stable day ordering, regardless of API order.
      const sortedUniqueDays = this.diseaseDays
        .slice()
        .filter((d) => !!d?.diseaseDate)
        .sort((a, b) => {
          const aMillis = a.diseaseDate?.toMillis?.() ?? 0;
          const bMillis = b.diseaseDate?.toMillis?.() ?? 0;
          return aMillis - bMillis;
        })
        // De-duplicate by day to avoid repeating day names/dates if the backend sends duplicates.
        .filter((d, idx, arr) => {
          const key = d.diseaseDate?.toISODate?.() ?? d.diseaseDate?.toString?.() ?? String(idx);
          return arr.findIndex((x) => (x.diseaseDate?.toISODate?.() ?? x.diseaseDate?.toString?.()) === key) === idx;
        });

      // Group days by risk level so each level gets its own row.
      const groupMap = new Map<number, typeof sortedUniqueDays>();
      for (const day of sortedUniqueDays) {
        const level = day.riskLevel ?? 0;
        if (!groupMap.has(level)) {
          groupMap.set(level, []);
        }
        groupMap.get(level)!.push(day);
      }

      const entries: ActiveRiskLevel[] = [];
      for (const [riskLevel, days] of groupMap) {
        const first = days[0].diseaseDate;
        const last = days[days.length - 1].diseaseDate;
        const firstStartOfDay = first?.startOf?.('day');
        const lastStartOfDay = last?.startOf?.('day');
        const lastMillis = lastStartOfDay?.toMillis?.() ?? 0;
        const dayNames: string[] = [];

        if (firstStartOfDay && lastStartOfDay) {
          for (
            let cursor = firstStartOfDay;
            cursor.toMillis() <= lastMillis;
            cursor = cursor.plus({ days: 1 })
          ) {
            dayNames.push(cursor.toFormat('EEEE'));
          }
        }

        const firstText = first?.toFormat('dd.MM.yyyy');
        const lastText = last?.toFormat('dd.MM.yyyy');

        entries.push({
          riskLevel,
          dateRangeDayNamesString: dayNames.join(', '),
          // Show a contiguous range instead of repeating all dates, collapsing to a
          // single date when the group covers one day.
          dateRangeDatesString: firstText === lastText ? firstText : `${firstText} - ${lastText}`,
        });
      }

      // Sort ascending so lower risk levels appear first.
      this.activeRiskLevels = entries.sort((a, b) => a.riskLevel - b.riskLevel);
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['diseaseDays'] && this.diseaseDays) {
      //Find right diseaseDate day in week and map to the right day to days array and set risk right days
      this.restartWeek();
    }
  }
}
